import { HotspotExpandDirection, HotspotPinComponent, Size, toJSONContent, Vector } from '@lessonup/pins-shared';
import { borderRadius, styled } from '@lessonup/ui-components';
import React, { useRef } from 'react';
import { TiptapViewer } from '../../components/Tiptap/TipTapViewer';
import { TipTapComponent } from '../../components/Tiptap/Tiptap.utils';
import { computeShadowFilter } from '../../utils';
import { usePinThemeContext } from '../../utils/pinTheme/PinThemeContext';
import {
  calculateContentHolderTranslateX,
  calculateContentHolderTranslateY,
  calculateFlipX,
  calculateFlipY,
} from './hotspotContentHolder.utils';
import { useContentEmptyCheck } from './useContentEmptyCheck';

export interface HotspotContentHolderProps {
  hotspotSize: Size;
  hotspotPosition: Vector;
  expandDirection: HotspotExpandDirection;
  showBody?: boolean;
  borderColor?: HotspotPinComponent['settings']['borderColor'];
  bodyText?: HotspotPinComponent['settings']['body'];
  title?: HotspotPinComponent['settings']['title'];
  backgroundColor?: string;
  textColor?: string;
  shadowSetting?: HotspotPinComponent['settings']['shadow'];
  componentId: string;
  pinId: string;
  Renderer?: TipTapComponent;
  onClickContent?: () => void;
}

const MAX_CONTENT_HOLDER_WIDTH = 320;
const MAX_CONTENT_HOLDER_HEIGHT = 256;

export const HotspotContentHolder: React.FC<HotspotContentHolderProps> = ({
  hotspotSize,
  hotspotPosition,
  expandDirection,
  showBody = false,
  bodyText,
  title,
  backgroundColor,
  textColor,
  borderColor,
  shadowSetting,
  componentId,
  pinId,
  Renderer,
  onClickContent,
}) => {
  const titleRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const { isContentEmpty: isTitleEmpty } = useContentEmptyCheck(titleRef);
  const { isContentEmpty: isBodyEmpty } = useContentEmptyCheck(bodyRef);
  const { typographies } = usePinThemeContext();

  // TODO ED-624: This is a workaround as we needed to store the content as string to stay within the maximum depth limit in Firestore
  // Remove once we retrieve the JSON from GraphQL
  const titleContent = typeof title === 'string' ? toJSONContent(title) : title;
  const bodyContent = typeof bodyText === 'string' ? toJSONContent(bodyText) : bodyText;

  const isXFlipped = calculateFlipX(expandDirection, hotspotSize, hotspotPosition);
  const isYFlipped = calculateFlipY(expandDirection, hotspotSize, hotspotPosition);

  if (titleContent === null) {
    console.log('Error parsing JSON content', title);
    return null;
  }
  if (bodyContent === null) {
    console.log('Error parsing JSON content', bodyText);
    return null;
  }

  return (
    <StyledWrapper
      onClick={onClickContent}
      style={{
        backgroundColor: backgroundColor,
        color: textColor,
        transform: `translate(${calculateContentHolderTranslateX(
          isXFlipped,
          hotspotSize
        )}, ${calculateContentHolderTranslateY(isYFlipped, hotspotSize)})`,
        boxShadow: computeShadowFilter(shadowSetting),
        display: (isTitleEmpty && isBodyEmpty) || (!showBody && isTitleEmpty) ? 'none' : 'inline-block',
        borderColor,
      }}
    >
      <StyledComponentText
        ref={titleRef}
        style={{ ...typographies.smallText, fontWeight: 700, display: isTitleEmpty ? 'none' : 'block' }}
      >
        {Renderer ? (
          <Renderer pinId={pinId} pinComponentId={componentId} fragmentKey="title" editable={false} active={false} />
        ) : (
          <TiptapViewer content={titleContent} />
        )}
      </StyledComponentText>
      <StyledComponentText
        ref={bodyRef}
        style={{
          ...typographies.smallText,
          display: isBodyEmpty || !showBody ? 'none' : 'block',
        }}
      >
        {Renderer ? (
          <Renderer pinId={pinId} pinComponentId={componentId} fragmentKey="body" editable={false} active={false} />
        ) : (
          <TiptapViewer content={bodyContent} />
        )}
      </StyledComponentText>
    </StyledWrapper>
  );
};

/**
 * We have to disable user-select because of TipTapComponent
 */
const StyledWrapper = styled.div`
  overflow-y: auto;
  word-wrap: break-word;
  user-select: none;
  position: absolute;
  padding: 6px; // This is 8px - 2px to correct for the border
  max-height: ${MAX_CONTENT_HOLDER_HEIGHT}px;
  max-width: ${MAX_CONTENT_HOLDER_WIDTH}px;
  width: max-content;
  border-width: 2px;
  border-style: solid;
  border-radius: ${borderRadius.rounded8};
`;

const StyledComponentText = styled.div`
  line-height: 150%;
  padding: 0 6px;
  * {
    margin: 0; // Reset browser styling
  }
`;
